import React, { useMemo, useState } from 'react'
import {
  Alert,
  Button,
  Card,
  Col,
  Input,
  message,
  Modal,
  Row,
  Select,
  Space,
  Statistic,
  Table,
  Tag,
  Typography,
  Upload,
} from 'antd'
import {
  CheckCircleOutlined,
  CloudUploadOutlined,
  DownloadOutlined,
  FileTextOutlined,
  SafetyCertificateOutlined,
  StopOutlined,
  WarningOutlined,
} from '@ant-design/icons'
import type { ColumnsType } from 'antd/es/table'
import type { UploadProps } from 'antd'
import {
  apiService,
  LeadImportClassification,
  LeadImportConfirmation,
  LeadImportPreview,
  LeadImportPreviewRow,
} from '../services/apiService'

const { Dragger } = Upload
const { Paragraph, Text, Title } = Typography
const MAX_FILE_BYTES = 5 * 1024 * 1024

const classificationLabels: Record<LeadImportClassification, string> = {
  will_import: 'Will import',
  duplicate: 'Duplicate',
  crm_owned: 'CRM owned',
  invalid: 'Invalid',
}

const classificationColors: Record<LeadImportClassification, string> = {
  will_import: 'green',
  duplicate: 'gold',
  crm_owned: 'blue',
  invalid: 'red',
}

function getErrorMessage(error: any): string {
  const data = error?.response?.data
  if (typeof data?.error === 'string') return data.error
  if (typeof data?.detail === 'string') return data.detail
  return error?.message || 'The import request failed.'
}

function saveBlob(blob: Blob, fileName: string) {
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = fileName
  document.body.appendChild(anchor)
  anchor.click()
  anchor.remove()
  URL.revokeObjectURL(url)
}

export function csvCell(value: unknown): string {
  let text = String(value ?? '')
  if (/^[=+\-@\t\r]/.test(text)) text = `'${text}`
  return `"${text.replace(/"/g, '""')}"`
}

function ImportLeads() {
  const [file, setFile] = useState<File | null>(null)
  const [batchNotes, setBatchNotes] = useState('')
  const [preview, setPreview] = useState<LeadImportPreview | null>(null)
  const [confirmation, setConfirmation] = useState<LeadImportConfirmation | null>(null)
  const [filter, setFilter] = useState<LeadImportClassification | 'all'>('all')
  const [validating, setValidating] = useState(false)
  const [confirming, setConfirming] = useState(false)
  const [downloadingTemplate, setDownloadingTemplate] = useState(false)

  const uploadProps: UploadProps = {
    accept: '.csv,text/csv',
    multiple: false,
    maxCount: 1,
    fileList: file
      ? [{ uid: file.name, name: file.name, size: file.size, type: file.type }]
      : [],
    beforeUpload: (candidate) => {
      if (!candidate.name.toLowerCase().endsWith('.csv')) {
        message.error('Only .csv files are accepted.')
        return Upload.LIST_IGNORE
      }
      if (candidate.size > MAX_FILE_BYTES) {
        message.error('CSV files must be 5 MB or smaller.')
        return Upload.LIST_IGNORE
      }
      setFile(candidate)
      setPreview(null)
      setConfirmation(null)
      return false
    },
    onRemove: () => {
      setFile(null)
      setPreview(null)
      setConfirmation(null)
      return true
    },
  }

  const filteredRows = useMemo(
    () => preview?.rows.filter(
      (row) => filter === 'all' || row.classification === filter,
    ) || [],
    [filter, preview],
  )

  const columns: ColumnsType<LeadImportPreviewRow> = [
    {
      title: 'CSV row',
      dataIndex: 'row_number',
      width: 90,
      sorter: (a, b) => a.row_number - b.row_number,
    },
    {
      title: 'Company',
      dataIndex: 'company_name',
      ellipsis: true,
    },
    {
      title: 'Result',
      dataIndex: 'classification',
      width: 135,
      filters: Object.entries(classificationLabels).map(([value, label]) => ({
        text: label,
        value,
      })),
      onFilter: (value, row) => row.classification === value,
      render: (classification: LeadImportClassification) => (
        <Tag color={classificationColors[classification]}>
          {confirmation && classification === 'will_import'
            ? 'Imported'
            : classificationLabels[classification]}
        </Tag>
      ),
    },
    {
      title: 'Reason',
      dataIndex: 'reason',
      render: (reason: string, row) => reason || (
        row.classification === 'will_import'
          ? (confirmation ? 'Imported' : 'Ready to import')
          : '—'
      ),
    },
  ]

  const validateFile = async () => {
    if (!file) {
      message.error('Choose a CSV file first.')
      return
    }
    setValidating(true)
    setConfirmation(null)
    try {
      const result = await apiService.validateLeadImport(file, batchNotes.trim())
      setPreview(result)
      setFilter('all')
      message.success('CSV validated. Review the rows before confirming.')
    } catch (error) {
      setPreview(null)
      message.error(getErrorMessage(error))
    } finally {
      setValidating(false)
    }
  }

  const confirmImport = () => {
    if (!preview) return
    Modal.confirm({
      title: `Import ${preview.summary.will_import} ready lead${
        preview.summary.will_import === 1 ? '' : 's'
      }?`,
      content: (
        <span>
          CRM ownership and duplicates will be checked again. Skipped rows will
          not be written.
        </span>
      ),
      okText: 'Confirm import',
      cancelText: 'Keep reviewing',
      onOk: async () => {
        setConfirming(true)
        try {
          const result = await apiService.confirmLeadImport(preview.import_id)
          setConfirmation(result)
          setPreview((current) => current && ({
            ...current,
            summary: result.summary,
            rows: result.rows,
          }))
          message.success(`Imported ${result.imported} lead${result.imported === 1 ? '' : 's'}.`)
        } catch (error) {
          message.error(getErrorMessage(error))
          throw error
        } finally {
          setConfirming(false)
        }
      },
    })
  }

  const downloadTemplate = async () => {
    setDownloadingTemplate(true)
    try {
      saveBlob(await apiService.downloadLeadImportTemplate(), 'lead-import-template.csv')
    } catch (error) {
      message.error(getErrorMessage(error))
    } finally {
      setDownloadingTemplate(false)
    }
  }

  const downloadReport = () => {
    if (!preview) return
    const lines = [
      ['row_number', 'company_name', 'classification', 'reason'].map(csvCell).join(','),
      ...preview.rows.map((row) => [
        row.row_number,
        row.company_name,
        row.classification,
        row.reason,
      ].map(csvCell).join(',')),
    ]
    saveBlob(
      new Blob([`\uFEFF${lines.join('\r\n')}\r\n`], {
        type: 'text/csv;charset=utf-8',
      }),
      `lead-import-${preview.import_id}-report.csv`,
    )
  }

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <div>
        <Title level={2} style={{ marginBottom: 4 }}>Import leads</Title>
        <Paragraph type="secondary" style={{ marginBottom: 0 }}>
          Upload a human-reviewed CSV. Every row needs a Palladium company key;
          CRM-owned and duplicate companies are skipped.
        </Paragraph>
      </div>

      <Alert
        type="info"
        showIcon
        message="Preview first, then confirm"
        description="Validation only creates a private staging batch. No leads enter the work queue until you confirm."
      />

      <Card title="1. Upload CSV">
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <Dragger {...uploadProps}>
            <p className="ant-upload-drag-icon"><CloudUploadOutlined /></p>
            <p className="ant-upload-text">Drop one CSV here or click to choose it</p>
            <p className="ant-upload-hint">
              CSV only · UTF-8 · up to 5 MB and 1,000 data rows
            </p>
          </Dragger>
          <Input.TextArea
            value={batchNotes}
            onChange={(event) => setBatchNotes(event.target.value)}
            placeholder="Optional batch notes (applied to every imported row)"
            maxLength={1000}
            showCount
            autoSize={{ minRows: 2, maxRows: 4 }}
          />
          <Space wrap>
            <Button
              type="primary"
              icon={<SafetyCertificateOutlined />}
              loading={validating}
              disabled={!file}
              onClick={validateFile}
            >
              Validate and preview
            </Button>
            <Button
              icon={<DownloadOutlined />}
              loading={downloadingTemplate}
              onClick={downloadTemplate}
            >
              Download template
            </Button>
          </Space>
        </Space>
      </Card>

      {preview && (
        <Card
          title={`2. Review ${preview.file_name}`}
          extra={(
            <Space wrap>
              <Select
                aria-label="Filter import rows"
                value={filter}
                style={{ width: 150 }}
                onChange={setFilter}
                options={[
                  { value: 'all', label: 'All rows' },
                  ...Object.entries(classificationLabels).map(([value, label]) => ({
                    value,
                    label,
                  })),
                ]}
              />
              <Button icon={<FileTextOutlined />} onClick={downloadReport}>
                Download report
              </Button>
            </Space>
          )}
        >
          <Row gutter={[12, 12]} style={{ marginBottom: 20 }}>
            <Col xs={12} md={4}>
              <Statistic title="Rows" value={preview.summary.total} />
            </Col>
            <Col xs={12} md={5}>
              <Statistic
                title={confirmation ? 'Imported' : 'Will import'}
                value={preview.summary.will_import}
                valueStyle={{ color: '#389e0d' }}
                prefix={<CheckCircleOutlined />}
              />
            </Col>
            <Col xs={12} md={5}>
              <Statistic title="Duplicates" value={preview.summary.duplicate} />
            </Col>
            <Col xs={12} md={5}>
              <Statistic
                title="CRM owned"
                value={preview.summary.crm_owned}
                prefix={<StopOutlined />}
              />
            </Col>
            <Col xs={12} md={5}>
              <Statistic
                title="Invalid"
                value={preview.summary.invalid}
                valueStyle={{ color: preview.summary.invalid ? '#cf1322' : undefined }}
                prefix={<WarningOutlined />}
              />
            </Col>
          </Row>

          <Table
            rowKey="row_number"
            columns={columns}
            dataSource={filteredRows}
            pagination={{ pageSize: 20, showSizeChanger: true }}
            scroll={{ x: 760 }}
            locale={{ emptyText: 'No rows match this filter.' }}
          />

          {confirmation ? (
            <Alert
              type="success"
              showIcon
              message={`Import complete: ${confirmation.imported} leads added`}
              description={
                confirmation.skipped_late_duplicates || confirmation.skipped_late_crm_owned
                  ? `${confirmation.skipped_late_duplicates} late duplicate(s) and ${confirmation.skipped_late_crm_owned} newly CRM-owned row(s) were skipped.`
                  : 'The confirmation-time checks found no new conflicts.'
              }
            />
          ) : (
            <div style={{ marginTop: 20 }}>
              <Button
                type="primary"
                size="large"
                icon={<CheckCircleOutlined />}
                loading={confirming}
                onClick={confirmImport}
              >
                Confirm import
              </Button>
              <Text type="secondary" style={{ marginLeft: 12 }}>
                CRM and duplicate checks run again before insert.
              </Text>
            </div>
          )}
        </Card>
      )}
    </Space>
  )
}

export default ImportLeads
